import { type User, userRequiresEmailValidation } from '@takeshape/schema';
import { fromJS } from 'immutable';
import { createSelector } from 'reselect';
import { NAME } from './constants';
import type { AuthSlice, AuthState } from './types';

// In the future we will want state to be AuthState, but it will involve adding
// auth to AuthState which will take some effort.
export const getDomainState = (state: AuthSlice): AuthState => state[NAME];

export const selectLoggedIn = createSelector(getDomainState, (domainState: AuthState) => domainState.userLoaded);

export type GetUserResult = {
  status: {
    loading: boolean;
    loaded: boolean;
    creating: boolean;
    created: boolean;
    error: string | null;
  };
  data: User | null;
};

export const getUser = createSelector(getDomainState, (state: AuthState): GetUserResult => {
  const status = {
    loading: state.userLoading,
    loaded: state.userLoaded,
    creating: state.userCreating,
    created: state.userCreated,
    error: state.userError
  };

  if (!status.loaded && !status.created) {
    return { status, data: null };
  }

  return {
    status,
    data: state.user?.toJS() ?? null
  };
});

export const selectUser = createSelector(getUser, (user) => fromJS(user));

export const getUserEntitlement = createSelector(
  selectUser,
  (_: unknown, props: { entitlement: string }) => props.entitlement,
  (user, entitlement): boolean | number | null => user.getIn(['data', 'entitlements', entitlement])
);

export const getUserId = createSelector(getDomainState, (state: AuthState) => state.user?.get('id'));

export const getUserEmail = createSelector(getDomainState, (state: AuthState) => state.user?.get('email'));

export const getUserPreferences = createSelector(getDomainState, (state: AuthState) => state.user?.get('preferences'));

export const getUserRequiresEmailValidation = createSelector(getDomainState, (state: AuthState) =>
  state.user ? userRequiresEmailValidation(state.user.toJS()) : null
);

export const getCliLoginToken = createSelector(getDomainState, (state: AuthState) => {
  return state.cliLoginToken;
});

export const getRespondToCliError = createSelector(getDomainState, (state: AuthState) => {
  return state.respondToCliError;
});

export const getAccessToken = createSelector(getDomainState, (state: AuthState) => {
  return state.accessToken;
});
